import { calculatePaymentRow, calibrateFirstPaymentDate, calculateSnowballData } from './payment-calculations';
import { dateHandler } from './date-utils';

/**
 * Utility functions for generating payment schedules
 * Focused on schedule generation and aggregation
 */

/**
 * Generates a complete payment schedule for a single account
 */
export const generatePaymentSchedule = (account) => {
    if (!account || !account.balance || account.balance <= 0) {
        return [];
    }

    let paymentDate = calibrateFirstPaymentDate(account.due_date);
    
    // Apply start date delay if specified - create new date to avoid mutation
    if (account.startDateDelayMonths && account.startDateDelayMonths > 0) {
        paymentDate = new Date(paymentDate);
        paymentDate.setMonth(paymentDate.getMonth() + account.startDateDelayMonths);
    }
    
    let paymentSchedule = [];
    let prevBalance = account.balance;
    let paymentNumber = 0;
    
    const MAX_PAYMENTS = 10000; // Safety limit to prevent infinite loops
    let iterationCount = 0;

    while (prevBalance > 0 && iterationCount < MAX_PAYMENTS) {
        paymentNumber++;
        iterationCount++;

        const paymentRow = calculatePaymentRow(
            paymentNumber,
            paymentDate,
            prevBalance,
            account
        );

        // Handle calculation errors
        if (paymentRow.error) {
            console.error(`Payment calculation error for account ${account.id}:`, paymentRow.error);
            break;
        }

        paymentSchedule.push(paymentRow);
        prevBalance = paymentRow.balance;
        paymentDate = new Date(paymentRow.date);
    }

    return paymentSchedule;
};

/**
 * Generates snowball data and payment schedule for an account
 */
export const generateAccountWithSnowball = (account, accountIndex, allAccounts, snowballStarterLump = 0) => {
    const updatedAccount = { ...account };
    
    // Add snowball data
    if (!isNaN(snowballStarterLump) && allAccounts) {
        updatedAccount.snowballData = calculateSnowballData(allAccounts, accountIndex, snowballStarterLump);
    }
    
    // Generate payment schedule
    updatedAccount.paymentSchedule = generatePaymentSchedule(updatedAccount);
    
    return updatedAccount;
};

/**
 * Creates a combined payment schedule across all accounts
 */
export const getCombinedPaymentSchedule = (accounts) => {
    if (!accounts || accounts.length === 0) {
        return [];
    }

    const combinedSchedule = new Map();
    const startingBalance = accounts.reduce((total, account) => total + account.balance, 0);

    // Process each account's payment schedule
    accounts.forEach(account => {
        if (!account.paymentSchedule) return;

        account.paymentSchedule.forEach(payment => {
            const paymentDate = new Date(payment.date);
            const year = paymentDate.getFullYear();
            const month = paymentDate.getMonth();
            const key = `${year}-${month.toString().padStart(2, '0')}`;
            const sortKey = new Date(year, month, 1).getTime();

            if (!combinedSchedule.has(key)) {
                combinedSchedule.set(key, {
                    yearMonth: key,
                    totalPayment: 0,
                    totalBalance: 0,
                    sortKey
                });
            }

            const existing = combinedSchedule.get(key);
            existing.totalPayment += payment.totalPayment;
            existing.totalBalance += payment.balance;
        });
    });

    // Convert to array and sort by date
    const scheduleArray = Array.from(combinedSchedule.values())
        .sort((a, b) => a.sortKey - b.sortKey);

    // Set initial balance for first entry
    if (scheduleArray.length > 0) {
        scheduleArray[0].totalBalance = startingBalance;
    }

    return scheduleArray.map(({ sortKey, ...rest }) => rest);
};

/**
 * Creates transaction data from a payment schedule
 */
export const createTransactionFromSchedule = (account, debitAccountID) => {
    if (!account.paymentSchedule || account.paymentSchedule.length === 0 || !debitAccountID) {
        return null;
    }

    const firstPayment = account.paymentSchedule[0];
    const lastPayment = account.paymentSchedule[account.paymentSchedule.length - 1];

    const transaction = {
        title: `${account.name} Payment`,
        description: `Payment for ${account.name}`,
        amount: -firstPayment.totalPayment, // Negative for expense
        category: 'Debt Payment',
        subcategory: `${account.type} Payment`,
        account: debitAccountID,
        startDate: dateHandler.toSubmitFormat(new Date(firstPayment.date)),
        endDate: dateHandler.toSubmitFormat(new Date(lastPayment.date)),
        isRecurring: 1,
        recurringType: 'monthly',
        dayOfWeek: null,
        dayOfMonthList: account.due_date,
        weekOfMonth: null,
        monthOfYear: null,
        separationCount: 1
    };

    // Create exceptions for payments that differ from the first payment amount
    const exceptions = account.paymentSchedule
        .filter(payment => payment.totalPayment !== firstPayment.totalPayment)
        .map(payment => ({
            transactionId: null, // Will be filled when transaction is created
            amount: -payment.totalPayment,
            startDate: new Date(payment.date),
            endDate: new Date(payment.date),
            instanceDate: new Date(payment.date),
            isRescheduled: 0,
            isCancelled: 0,
            isPaid: 0,
            highlight: 0,
            daysAdjusted: 0
        }));

    return { transaction, exceptions };
};

/**
 * Creates transactions and exceptions for all accounts
 */
export const createTransactionsAndExceptions = (account, debitAccountID) => {
    return createTransactionFromSchedule(account, debitAccountID);
};

/**
 * Calculates aggregate statistics for all payment schedules
 */
export const calculateAggregateStatistics = (accounts) => {
    if (!accounts || accounts.length === 0) {
        return null;
    }

    let totalPaid = 0;
    let totalInterestPaid = 0;
    let totalStartingBalance = 0;
    let totalMonths = 0;
    
    // Track all unique payment dates across all accounts
    const allPaymentDates = new Set();
    
    // Create account breakdown data for SnowballProgress component
    const accountsBreakdown = [];

    accounts.forEach((account, index) => {
        totalStartingBalance += account.balance;

        if (account.paymentSchedule && account.paymentSchedule.length > 0) {
            // Calculate account-specific totals for breakdown
            const schedule = account.paymentSchedule;
            const accountTotalPayments = schedule.reduce((sum, payment) => sum + payment.totalPayment, 0);
            const accountTotalInterest = schedule.reduce((sum, payment) => sum + payment.interestPaid, 0);
            
            // Add to accounts breakdown
            accountsBreakdown.push({
                id: account.id,
                name: account.name,
                balance: account.balance,
                months: schedule.length,
                totalPayments: accountTotalPayments,
                totalInterest: accountTotalInterest,
                interestRate: account.apr,
                order: account.index !== undefined ? account.index : index
            });

            // Sum all payments and interest for this account
            account.paymentSchedule.forEach((payment) => {
                totalPaid += payment.totalPayment;
                totalInterestPaid += payment.interestPaid;
                
                // Track all payment dates
                const paymentDate = payment.date;
                const dateObj = new Date(paymentDate);
                const monthKey = `${dateObj.getFullYear()}-${String(dateObj.getMonth() + 1).padStart(2, '0')}`;
                allPaymentDates.add(monthKey);
            });

            // Track longest payoff time
            totalMonths = Math.max(totalMonths, account.paymentSchedule.length);
        }
    });

    // Create monthly payment summary by iterating through each unique month
    const paymentsSummary = [];
    const sortedDates = Array.from(allPaymentDates).sort();
    
    sortedDates.forEach(monthKey => {
        const [year, month] = monthKey.split('-');
        const monthDate = new Date(parseInt(year), parseInt(month) - 1, 1);
        
        let totalPayment = 0;
        let totalBalance = 0;

        // Sum up payments and balances for this month across all accounts
        accounts.forEach(account => {
            if (account.paymentSchedule && account.paymentSchedule.length > 0) {
                account.paymentSchedule.forEach(payment => {
                    const paymentDate = new Date(payment.date);
                    const paymentMonthKey = `${paymentDate.getFullYear()}-${String(paymentDate.getMonth() + 1).padStart(2, '0')}`;
                    
                    if (paymentMonthKey === monthKey) {
                        totalPayment += payment.totalPayment;
                        totalBalance += payment.balance;
                    }
                });
            }
        });

        paymentsSummary.push({
            totalPayment,
            totalBalance,
            date: monthKey,
            sortDate: monthDate.getTime()
        });
    });

    const totalPrincipal = totalStartingBalance;
    const averageMonthlyPayment = totalMonths > 0 ? totalPaid / totalMonths : 0;
    const interestSavingsPercentage = totalStartingBalance > 0 
        ? (totalInterestPaid / totalStartingBalance) * 100 
        : 0;

    // Sort accounts breakdown by order
    accountsBreakdown.sort((a, b) => a.order - b.order);

    // Create streamlined payment schedules with only essential data
    const paymentSchedules = {};
    accounts.forEach(account => {
        if (account.paymentSchedule && account.paymentSchedule.length > 0) {
            paymentSchedules[account.id] = {
                accountName: account.name,
                payments: account.paymentSchedule.map(payment => ({
                    paymentNumber: payment.paymentNumber,
                    date: payment.date,
                    payment: payment.totalPayment || payment.payment,
                    balance: payment.balance
                }))
            };
        }
    });

    return {
        totalPaid,
        totalInterestPaid,
        totalPrincipal,
        totalStartingBalance,
        totalMonths,
        averageMonthlyPayment,
        interestSavingsPercentage,
        payoffTimeYears: totalMonths / 12,
        paymentsSummary,
        accountsBreakdown, // Added for SnowballProgress component
        paymentSchedules // Added streamlined schedules
    };
};